import React from "react";
import { PlaylistInterface, ApiHelper, PendingCommand, UserHelper, DateHelper, ToastHelper } from "../../helpers";
import { Row, Col, FormControl, FormGroup, FormLabel, Button, FormCheck, Alert } from "react-bootstrap"
import { PlaylistMessages } from "./PlaylistMessages"
import { InputBox, Loading, PreviewModal, } from "../../components"
import { Link, Navigate } from "react-router-dom";
import { PlaylistAnswers } from ".";

interface Props {
  subscriptionData?: any,
  playlist: PlaylistInterface,
  refreshSidebar: () => void,
  pendingCommand: PendingCommand,
  sendCommand: (cmd: PendingCommand) => void
}

export const PlaylistEdit: React.FC<Props> = props => {

  const [playlist, setPlaylist] = React.useState<PlaylistInterface>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [redirect, setRedirect] = React.useState("");
  const [messagesCommand, setMesssagesCommand] = React.useState<PendingCommand>(null);
  const [answersCommand, setAnswersCommand] = React.useState<PendingCommand>(null);
  const [showPreview, setShowPreview] = React.useState(false);
  const [submitProgress, setSubmitProgress] = React.useState(0);
  const [showDates, setShowDates] = React.useState(false);


  const handleChange = (e: any) => {
    let p: PlaylistInterface = { ...playlist };
    let val = e.currentTarget.value;
    switch (e.currentTarget.id) {
      case "name": p.name = val; break;
      case "playOrder": p.playOrder = val; break;
      case "externalSourceUrl": p.externalSourceUrl = val; break;
      case "crossDepartment":
        const chk: any = document.getElementById('crossDepartment');
        p.crossDepartment = chk.checked;
        break;
      case "startDate": p.startDate = DateHelper.convertToDate(val); break;
      case "endDate": p.endDate = DateHelper.convertToDate(val); break;
    }
    setPlaylist(p);
  }

  const validate = () => {
    if (!playlist.name) {
      ToastHelper.error("Please enter a playlist name.");
      return false;
    }
    return true;
  }

  const handleSaved = (playlistId: number) => {
    setIsSubmitting(false);
    props.refreshSidebar();
    ToastHelper.success("Changes saved successfully");
    if (props.playlist.id === 0) setRedirect("/playlists");
  }

  const handleSave = () => {
    if (validate()) {
      setIsSubmitting(true);
      playlist.name = playlist.name.trim();
      ApiHelper.post<PlaylistInterface[]>("/playlists", [playlist])
        .then((data: PlaylistInterface[]) => {
          if (playlist.mode === "external") handleSaved(playlist.id);
          else {
            if (playlist.subscriptionId || playlist.mode === "slideshow") setAnswersCommand({ action: "save", parameter: data[0].id.toString() });
            else setMesssagesCommand({ action: "save", parameter: data[0].id.toString() });
          }
        })
        .catch((error) => {
          console.error("Error saving playlist:", error);
          ToastHelper.error("Failed to save playlist. Please try again.");
          setIsSubmitting(false);
        });
    }
  }

  const getDeleteFunction = () => (playlist?.id > 0) ? handleDelete : undefined

  const handleDelete = () => {
    if (window.confirm("Are you sure you wish to permanently delete this playlist?")) {
      ApiHelper.delete("/playlists/" + playlist.id).then(() => {
        props.refreshSidebar();
        setRedirect("/playlists");
      });
    }
  }

  const handleDuplicate = () => {
    ApiHelper.post<PlaylistInterface>("/playlists/" + playlist.id + "/duplicate", {})
      .then((data: PlaylistInterface) => {
        props.refreshSidebar();
        setRedirect("/playlists/" + data.id);
        ToastHelper.success("Playlist duplicated successfully");
      })
      .catch((error) => {
        console.error("Error duplicating playlist:", error);
        ToastHelper.error("Failed to duplicate playlist. Please try again.");
      });
  }

  const messagesSaved = () => { handleSaved(playlist.id); }

  const answersSaved = (playlistId: number) => {
    ApiHelper.post("/playableMessages/playlist/" + playlist.id, {})
      .then(() => {
        return ApiHelper.post("/playableMessages/playlist/" + playlistId, {});
      })
      .then(() => {
        handleSaved(playlistId);
      })
      .catch((error) => {
        console.error("Error saving playable messages:", error);
        ToastHelper.error("Failed to save playlist messages. Please try again.");
        setIsSubmitting(false);
      });
  }

  const handlePlaylistMessagesCommand = (cmd: PendingCommand) => {
    if (cmd.action === "subset" || cmd.action === "conditions") {
      props.sendCommand(cmd);
    }
  }

  const getMessages = () => {
    if (!playlist.subscriptionId && playlist.mode !== "external" && playlist.mode !== "slideshow") {
      return <PlaylistMessages playlistId={playlist.id} pendingCommand={messagesCommand} saveCallback={messagesSaved} sendCommand={handlePlaylistMessagesCommand} />
    } else return <></>
  }


  const handleAnswersCommand = (cmd: PendingCommand) => {
    if (cmd.action === "updateProgress") setSubmitProgress(cmd.parameter as number);
    else props.sendCommand(cmd); //updatePreview
  }

  const getAnswers = () => {
    if (playlist.subscriptionId || playlist.mode === "slideshow") {
      return <PlaylistAnswers subscriptionData={props.subscriptionData} playlist={playlist} pendingCommand={answersCommand} saveCallback={answersSaved} sendCommand={handleAnswersCommand} />

    } else return <></>
  }

  const handleParentCommand = () => {
    if (props.pendingCommand) setMesssagesCommand(props.pendingCommand);
  }


  const getDuplicateButton = () => {
    if (props.playlist?.id > 0) return <Button variant="outline-secondary" size="sm" className="me-1" onClick={(e) => { e.preventDefault(); handleDuplicate(); }}>Duplicate</Button>
  }

  const getPreviewButton = () => {
    if (props.playlist?.id > 0) return <Button variant="info" size="sm" onClick={(e) => { e.preventDefault(); setShowPreview(!showPreview) }}>Preview</Button>
  }

  const getPreviewModal = () => {
    if (showPreview) return <PreviewModal shape={playlist?.shape} playlistId={playlist?.id} onClose={() => { setShowPreview(false) }} />
  }

  const getPlayOrder = () => {
    if (playlist.mode === "normal") {
      return (<Col>
        <FormGroup className="mb-3">
          <FormLabel htmlFor="mode">Play Order</FormLabel>
          <FormControl as="select" name="playOrder" id="playOrder" value={playlist.playOrder || ""} onChange={handleChange} >
            <option value="sequential">Sequential</option>
            <option value="random">Random</option>
          </FormControl>
        </FormGroup>
      </Col>);
    }
  }

  const getExternalUrl = () => {
    if (playlist.mode === "external") {
      return (<Col sm={12}>
        <FormGroup className="mb-3">
          <FormLabel htmlFor="mode">External Feed Url</FormLabel>
          <FormControl id="externalSourceUrl" value={playlist.externalSourceUrl} onChange={handleChange} />
        </FormGroup>
        <Alert variant="info"><b>Note:</b> This is only for a specific data feed format like what is used on lessons.church.  To show your own website, use the <Link to="/messages/0?templateId=9&shape=16:9">Show Website</Link> message template instead.</Alert>
      </Col>)
    }
  }


  const getChecks = () => {
    const result: React.ReactElement[] = [];
    if (UserHelper.departments.length > 1) {
      result.push(
        <FormGroup key="crossDepartmentCheck" controlId="formBasicCheckbox" className="mb-3">
          <FormCheck id="crossDepartment" type="checkbox" label="Show in all departments" onChange={handleChange} checked={playlist?.crossDepartment} />
        </FormGroup>
      );
    }
    return result;
  }

  const getDateLabel = () => {
    if (playlist) {
      if ((playlist.mode === "normal" || playlist.mode === "slideshow") && !showDates) {
        let label = "Limit dates";
        if (playlist.startDate) {
          if (playlist.endDate) label = DateHelper.prettyDate(DateHelper.convertToDateOnly(playlist.startDate)) + " - " + DateHelper.prettyDate(DateHelper.convertToDateOnly(playlist.endDate));
          else label = "Start: " + DateHelper.prettyDate(DateHelper.convertToDateOnly(playlist.startDate));
        } else if (playlist.endDate) {
          label = "End: " + DateHelper.prettyDate(DateHelper.convertToDateOnly(playlist.endDate));
        }
        return <span ><a href="about:blank" onClick={(e) => { e.preventDefault(); setShowDates(true) }} style={{ color: "#999999" }}>{label}</a></span>
      }
    }
  }

  const getDates = () => {
    if (showDates) {
      const startDateString = (playlist?.startDate) ? DateHelper.formatHtml5Date(DateHelper.convertToDateOnly(playlist.startDate)) : "";
      const endDateString = (playlist?.endDate) ? DateHelper.formatHtml5Date(DateHelper.convertToDateOnly(playlist.endDate)) : "";
      return (
        <Row>
          <Col>
            <FormGroup className="mb-3">
              <FormLabel htmlFor="startDate">Start Date</FormLabel>
              <FormControl id="startDate" value={startDateString} onChange={handleChange} type="date" />
              <a href="about:blank" onClick={(e) => { e.preventDefault(); const p = { ...playlist }; p.startDate = null; setPlaylist(p); }} >Clear</a>
            </FormGroup>
          </Col>
          <Col>
            <FormGroup className="mb-3">
              <FormLabel htmlFor="endDate">End Date</FormLabel>
              <FormControl id="endDate" value={endDateString} onChange={handleChange} type="date" />
              <a href="about:blank" onClick={(e) => { e.preventDefault(); const p = { ...playlist }; p.endDate = null; setPlaylist(p); }} >Clear</a>
            </FormGroup>
          </Col>
        </Row>
      );
    }
  }



  React.useEffect(() => { setPlaylist(props.playlist); setShowDates(false); setRedirect(""); }, [props.playlist]);
  React.useEffect(handleParentCommand, [props.pendingCommand]);



  if (redirect !== "") return <Navigate to={redirect} replace />
  else if (!playlist) return <Loading />
  else return (
    <>
      <InputBox headerIcon="fas fa-list-ul" headerText={"Edit Playlist"} isSubmitting={isSubmitting} submitProgress={submitProgress} saveFunction={handleSave} deleteFunction={getDeleteFunction()} deleteText="Delete playlist" headerActionContent={<>{getDuplicateButton()}{getPreviewButton()}</>} >
        <Row>
          <Col md={6}>{getChecks()}</Col>
          <Col md={6}>{getDateLabel()}</Col>
        </Row>
        <Row>
          <Col>
            <FormGroup className="mb-3">
              <FormLabel htmlFor="name">Name</FormLabel>
              <FormControl name="name" id="name" value={playlist.name || ""} onChange={handleChange} />
            </FormGroup>
          </Col>
          {getPlayOrder()}
          {getExternalUrl()}
        </Row>
        {getDates()}
        {getMessages()}
        {getAnswers()}
      </InputBox >
      {getPreviewModal()}
    </>
  );
}